import Vue from 'vue';
import { ref, watch } from '@vue/composition-api';
import { useAgentWebSocket } from './useAgentWebSocket';

const DISCONNECTED_TIP_ID = 'system-disconnected';

/**
 * Vue2 版聊天逻辑 composable
 * 移植自 doctor-agent-web 的 useChat.ts，去掉 Pinia 依赖，改为本地 ref 管理状态
 *
 * @param {Object} authInfo - { sessionId, doctorId, hospitalId, conversationId }
 */
export function useAgentChat(authInfo) {
  const { status, connect, send, onMessage, disconnect, stopReconnect } = useAgentWebSocket(authInfo);

  // ---- 消息状态（对应 chat store） ----
  const messages = ref([]);
  const isTyping = ref(false);
  const isSending = ref(false);
  const isGenerating = ref(false); // AI 正在生成回复（流式输出中）
  const errorBanner = ref('');
  // 记录当前正在生成的 messageId，用于 stop_generation
  let currentGeneratingMessageId = null;
  let errorBannerTimer = null;

  // ---- conversationId 管理 ----
  const CONV_STORAGE_KEY = 'doctor-agent-conversation-id';

  function loadConversationId() {
    try { return localStorage.getItem(CONV_STORAGE_KEY) || ''; } catch { return ''; }
  }

  function saveConversationId(id) {
    try { localStorage.setItem(CONV_STORAGE_KEY, id); } catch { /* ignore */ }
  }

  function generateConversationId() {
    const id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    authInfo.conversationId = id;
    saveConversationId(id);
    return id;
  }

  // 初始化 conversationId
  if (!authInfo.conversationId) {
    authInfo.conversationId = loadConversationId();
  }

  // ---- 消息操作 ----
  /**
   * @param {string} text
   * @param {string} messageStatus
   * @param {Array<{fileName: string, mimeType: string}>} [attachments] 附件元信息（仅用于前端展示，不含 base64 数据）
   */
  function addUserMessage(text, messageStatus, attachments) {
    const msg = {
      id: `user-${Date.now()}`,
      role: 'user',
      text,
      timestamp: Date.now(),
      done: true,
      status: messageStatus,
      // 图片附件保留 base64 data 用于气泡缩略图展示，文档类附件只保留元信息（节省内存）
      attachments: attachments && attachments.length > 0
        ? attachments.map((a) => ({
            fileName: a.fileName,
            mimeType: a.mimeType,
            // 图片类型保留 base64，文档类型不保留
            ...(a.mimeType && a.mimeType.startsWith('image/') ? { data: a.data } : {}),
          }))
        : undefined,
    };
    messages.value.push(msg);
    return msg;
  }

  function addAgentMessage(text, messageId, done = true) {
    const msg = {
      id: messageId || `agent-${Date.now()}`,
      role: 'agent',
      text,
      timestamp: Date.now(),
      done,
    };
    messages.value.push(msg);
    return msg;
  }

  function updateAgentMessage(messageId, text, done = false, append = false) {
    const msg = messages.value.find((m) => m.id === messageId);
    if (msg) {
      if (append) {
        if (msg._baseText === undefined) {
          msg._baseText = msg.text;
        }
        msg.text = msg._baseText + '\n\n' + text;
      } else {
        msg.text = text;
      }
      msg.done = done;
      if (done) {
        msg._baseText = msg.text;
        msg.progressText = undefined;
      }
    }
  }

  function updateProgressText(messageId, text) {
    const msg = messages.value.find((m) => m.id === messageId);
    if (msg) {
      msg.progressText = text;
    }
  }

  function updateMessageStatus(messageId, messageStatus) {
    const msg = messages.value.find((m) => m.id === messageId);
    if (msg) {
      msg.status = messageStatus;
    }
  }

  function removeMessage(messageId) {
    const index = messages.value.findIndex((m) => m.id === messageId);
    if (index !== -1) {
      messages.value.splice(index, 1);
    }
  }

  /**
   * 向指定消息添加或更新卡片（通过 cardId 去重）
   * @param {string} messageId
   * @param {{ id: string, type: string, [key: string]: any }} card
   */
  function addOrUpdateCardToMessage(messageId, card) {
    const msg = messages.value.find((m) => m.id === messageId);
    if (!msg) return;
    if (!msg.cards) {
      // Vue2 响应式：新增属性必须用 Vue.set，否则视图不更新
      Vue.set(msg, 'cards', []);
    }
    const idx = msg.cards.findIndex((c) => c.id === card.id);
    if (idx !== -1) {
      // 用 splice 触发 Vue2 响应式
      msg.cards.splice(idx, 1, card);
    } else {
      msg.cards.push(card);
    }
  }

  function clearMessages() {
    messages.value = [];
  }

  // ---- 错误横幅 ----
  function showErrorBanner(text) {
    errorBanner.value = text;
    if (errorBannerTimer) clearTimeout(errorBannerTimer);
    errorBannerTimer = setTimeout(() => {
      errorBanner.value = '';
      errorBannerTimer = null;
    }, 5000);
  }

  // 连接恢复后移除断线提示
  watch(status, (newStatus, oldStatus) => {
    if (newStatus === 'connected' && oldStatus !== 'connected') {
      removeMessage(DISCONNECTED_TIP_ID);
    }
  });

  // ---- 初始化 WS 消息处理 ----
  function init() {
    onMessage((data) => {
      const msg = data;
      const messageId = msg.messageId;

      if (msg.type === 'reply') {
        isTyping.value = false;
        // 断开重连时，若对话已有消息则忽略后端重复推送的欢迎语
        if (messageId && messageId.startsWith('welcome-') && messages.value.length > 0) {
          return;
        }
        const isDone = msg.done !== undefined ? !!msg.done : true;
        const isAppend = !!msg.append;
        const existing = messageId ? messages.value.find((m) => m.id === messageId) : undefined;
        if (existing) {
          updateAgentMessage(messageId, msg.text || '', isDone, isAppend);
        } else {
          addAgentMessage(msg.text || '', messageId, isDone);
          if (!isDone) {
            currentGeneratingMessageId = messageId;
          }
        }
        if (isDone) {
          isGenerating.value = false;
          currentGeneratingMessageId = null;
        } else {
          isGenerating.value = true;
          if (messageId) currentGeneratingMessageId = messageId;
        }
      } else if (msg.type === 'generation_stopped') {
        // 后端确认已中止生成
        isTyping.value = false;
        isGenerating.value = false;
        // 将正在生成的消息标记为完成（保留已输出的内容）
        if (currentGeneratingMessageId) {
          const genMsg = messages.value.find((m) => m.id === currentGeneratingMessageId);
          if (genMsg && !genMsg.done) {
            genMsg.done = true;
            genMsg.progressText = undefined;
          }
          currentGeneratingMessageId = null;
        }
      } else if (msg.type === 'progress') {
        isTyping.value = false;
        const existing = messageId ? messages.value.find((m) => m.id === messageId) : undefined;
        if (!existing) {
          addAgentMessage('', messageId, false);
        }
        updateProgressText(messageId, msg.text || '');
      } else if (msg.type === 'card') {
        // 分离推送的卡片消息（如写入确认卡片）
        isTyping.value = false;
        if (messageId) {
          const existing = messages.value.find((m) => m.id === messageId);
          if (!existing) {
            addAgentMessage('', messageId, false);
          }
          const cardType = msg.cardType;
          const cardData = msg.cardData;
          const cardId = String(msg.cardId || `${messageId}-${cardType}`);
          if (cardType && cardData) {
            const card = { id: cardId, cardType, cardData };
            addOrUpdateCardToMessage(messageId, card);
          }
        }
      } else if (msg.type === 'typing') {
        isTyping.value = true;
        isGenerating.value = true;
      } else if (msg.type === 'error') {
        isTyping.value = false;
        isGenerating.value = false;
        currentGeneratingMessageId = null;
        const text = msg.text || '发生错误，请稍后重试';
        // 登录失效（not logged）：停止自动重连，提示用户刷新页面
        const isNotLogged = text.includes('not logged') || text.includes('code=100');
        if (isNotLogged) {
          stopReconnect();
          addAgentMessage('⚠️ 登录状态已失效，请刷新页面重新登录', undefined, true);
          showErrorBanner('登录已失效，请刷新页面重新登录');
        } else {
          addAgentMessage(`⚠️ ${text}`, undefined, true);
          showErrorBanner(text);
        }
      } else if (msg.type === 'session_expired') {
        isTyping.value = false;
        stopReconnect();
        addAgentMessage('⚠️ 登录状态已过期，请刷新页面重新登录', undefined, true);
        showErrorBanner('登录已过期，请刷新页面重新登录');
      } else if (msg.type === 'session_info') {
        if (msg.conversationId) {
          authInfo.conversationId = msg.conversationId;
          saveConversationId(msg.conversationId);
        }
      } else if (msg.type === 'session_cleared') {
        isTyping.value = false;
        if (msg.conversationId) {
          authInfo.conversationId = msg.conversationId;
          saveConversationId(msg.conversationId);
        }
      }
    });

    connect();
  }

  function stopGeneration() {
    if (!isGenerating.value && !isTyping.value) return;
    send({
      type: 'stop_generation',
      messageId: currentGeneratingMessageId || undefined,
    });
    // 乐观更新：立即重置状态，不等后端确认
    isTyping.value = false;
    isGenerating.value = false;
    if (currentGeneratingMessageId) {
      const genMsg = messages.value.find((m) => m.id === currentGeneratingMessageId);
      if (genMsg && !genMsg.done) {
        genMsg.done = true;
        genMsg.progressText = undefined;
      }
      currentGeneratingMessageId = null;
    }
  }

  /**
   * 发送消息，支持携带文件附件
   * @param {string} text 消息文本
   * @param {Array<{data: string, mimeType: string, fileName?: string}>} [attachments] 文件附件列表（base64 编码）
   */
  function sendMessage(text, attachments) {
    const hasText = text && text.trim().length > 0;
    const hasAttachments = attachments && attachments.length > 0;
    if ((!hasText && !hasAttachments) || isSending.value) return;
    isSending.value = true;
    const userMsg = addUserMessage(text, 'pending', attachments);

    const wsPayload = {
      type: 'message',
      text,
      sessionId: authInfo.sessionId,
      doctorId: authInfo.doctorId,
      hospitalId: authInfo.hospitalId,
    };

    // 有附件时才加入 attachments 字段，避免空数组增大消息体
    if (attachments && attachments.length > 0) {
      wsPayload.attachments = attachments;
    }

    const success = send(wsPayload);

    if (!success) {
      updateMessageStatus(userMsg.id, 'failed');
      if (!messages.value.some((m) => m.id === DISCONNECTED_TIP_ID)) {
        addAgentMessage('⚠️ 连接已断开，正在重连...', DISCONNECTED_TIP_ID, true);
      }
    }
    isSending.value = false;
  }

  function retryMessage(messageId) {
    const msg = messages.value.find((m) => m.id === messageId);
    if (!msg || msg.role !== 'user' || msg.status !== 'failed') return;
    const text = msg.text;
    removeMessage(messageId);
    sendMessage(text);
  }

  function newConversation() {
    generateConversationId();
    clearMessages();
    disconnect();
    connect();
  }

  function clearConversation() {
    send({ type: 'clear_session' });
  }

  function sendApprovalDecision(approvalId, decision) {
    send({
      type: 'approval-decision',
      approvalId,
      decision,
    });
  }

  /**
   * 发送通用卡片交互消息（quick-options 选择 / text-input 提交 / 取消）
   * @param {string} cardId
   * @param {'select'|'submit'|'cancel'} action
   * @param {string|undefined} value
   */
  function sendCardAction(cardId, action, value) {
    send({
      type: 'card-action',
      cardId,
      action,
      value,
    });
  }

  return {
    status,
    messages,
    isTyping,
    isSending,
    isGenerating,
    errorBanner,
    init,
    sendMessage,
    stopGeneration,
    retryMessage,
    disconnect,
    newConversation,
    clearConversation,
    sendApprovalDecision,
    sendCardAction,
  };
}
